// eslint-disable-next-line import/named
import { cache } from '../../cache-impl/cache-registry';
import { clientContainerManagerFactory } from '../client-ui-adaptor';
import { ClientSummaryTable } from './franchisee-client-list-table';
import { ContactContainer, createNewContact } from '../../../clients/data/contact-container-manager';
import { ContactSummaryTable } from '../../../clients/views/contact-list-table';
import { createNewClient } from '../data/franchisee-client-container';
import {
  createNewPaymentProfile,
  PaymentProfileContainer
} from '../../../franchisee/data/payment-profile-container-manager';
import {
  createNewTermsConditions,
  TermsConditionsContainer
} from '../../../franchisee/data/terms-conditions-container-manager';
import { customElement } from 'lit/decorators.js';
import { getApiFactory } from '../../../api/api-injector';
import { getInternalId } from '../../../../webmodule-common/other/ui/databinding/databinding';
import { html } from 'lit';
import { IconRefresh } from '../../../../webmodule-common/other/ui/icons/icon-refresh';
import { LangFranchiseeClientListView } from '../../../../localization/scripts/dealer-franchisee/clients/views/franchisee-client-list-view-lang';
import { PageControl } from '../../../../webmodule-common/other/ui/page-control';
import { PaymentProfileSummaryTable } from '../../../franchisee/view/payment-profile-list-table';
import { singleSupplierId } from '../../../quotes/quote-service';
import { TermsConditionsSummaryTable } from '../../../franchisee/view/terms-conditions-list-table';
import { userDataStore } from '../../common/current-user-data-store';
import { v6BootLoaderWithFailMsg } from '../../../v6config/v6config';
import { ViewBase } from '../../../../webmodule-common/other/ui/view-base';
import type { ClientApi } from '../../../api/client-api';
import type { ClientViewOptions } from '../../../clients/views/client-view';
import type { ContactView, ContactViewOptions } from '../../../clients/views/contact-view';
import type { DataCacheGeneric } from '../../../../webmodule-common/cache/generic-data-cache';
import type { EventTemplate, Snippet } from '../../../../webmodule-common/other/ui/events';
import type { FranchiseeClientView } from './franchisee-client-view';
import type { PageControlOptions, PageManager } from '../../../../webmodule-common/other/ui/page-control';
import type { PaymentProfileApi } from '../../../api/payment-profile-api';
import type { PaymentProfileSummary, TermsAndConditionsSummary } from '../../../api/dealer-api-interface-franchisee';
import type { PaymentProfileView, PaymentProfileViewOptions } from '../../../franchisee/view/payment-profile-view';
import type { TermsAndConditionsApi } from '../../../api/terms-and-conditions-api';
import type { TermsConditionsView, TermsConditionsViewOptions } from '../../../franchisee/view/terms-conditions-view';
import type { ViewContactSummary } from '../../../api/dealer-api-interface-client';

export type ClientViewFactory = (options: ClientViewOptions) => Promise<FranchiseeClientView>;
export type ContactViewFactory = (options: ContactViewOptions) => Promise<ContactView>;
export type PaymentProfileViewFactory = (options: PaymentProfileViewOptions) => Promise<PaymentProfileView>;
export type TermsConditionsViewFactory = (options: TermsConditionsViewOptions) => Promise<TermsConditionsView>;

export interface FranchiseeClientListViewOptions {
  clientOwnerId: string;
  clientViewFactory: ClientViewFactory;
  contactViewFactory: ContactViewFactory;
  paymentProfileFactory: PaymentProfileViewFactory;
  termsConditionsFactory: TermsConditionsViewFactory;
}

@customElement('wm-franchiseeclientlistview')
export class FranchiseeClientListView extends ViewBase {
  tables: (ClientSummaryTable | ContactSummaryTable | PaymentProfileSummaryTable | TermsConditionsSummaryTable)[];
  clientOwnerId: string;
  clientApi: ClientApi = getApiFactory().client();
  paymentProfileApi: PaymentProfileApi = getApiFactory().paymentProfile();
  termsConditionsApi: TermsAndConditionsApi = getApiFactory().termsAndConditions();
  elementId: string;
  paymentProfileCache: DataCacheGeneric = cache().paymentProfile;
  clientCache: DataCacheGeneric = cache().client;
  pageControl: PageControl;
  contactViewFactory: ContactViewFactory;
  clientViewFactory: ClientViewFactory;
  paymentProfileFactory: PaymentProfileViewFactory;
  termsConditionsFactory: TermsConditionsViewFactory;
  addButtonTitle: Snippet;

  constructor(options: FranchiseeClientListViewOptions) {
    super();
    this.elementId = `client-list-view-${getInternalId()}`;
    this.clientOwnerId = options.clientOwnerId;
    this.clientViewFactory = options.clientViewFactory;
    this.contactViewFactory = options.contactViewFactory;
    this.paymentProfileFactory = options.paymentProfileFactory;
    this.termsConditionsFactory = options.termsConditionsFactory;

    this.tables = this.initializeTables();
    this.addButtonTitle = this.tables[0].addButtonTitle();

    this.pageControl = this.createPageControl();
  }

  public async setActiveTabByHash() {
    await this.pageControl.applyWindowHash();
  }

  createPageControl(): PageControl {
    const getInitialPageManagers = (): PageManager[] => {
      return this.tables.map(table => {
        const pm: PageManager = {
          caption: table.eventTitle,
          canClose: () => Promise.resolve(false),
          canLeave: () => Promise.resolve(true),
          hasDelete: () => false,
          onEnter: async () => {
            this.addButtonTitle = table.addButtonTitle();
          },
          content: () => {
            return table;
          },
          data: table,
          pageFragment: table.pageFragment
        };
        return pm;
      });
    };

    const options: PageControlOptions = {
      defaultTabIndex: 0,
      menuIcons: [
        {
          event: async () => {
            const activePage = this.pageControl.activePage;

            if (activePage) {
              const table = activePage.data as ClientSummaryTable | ContactSummaryTable | PaymentProfileSummaryTable;
              await table.refreshData();
            }

            return true;
          },
          caption: () => html`${new IconRefresh()}`
        }
      ],
      plusPage: {
        caption: () => this.addButtonTitle,
        event: async () => {
          const activePage = this.pageControl.activePage;
          if (!(await userDataStore.singleSupplierTACApproved())) return false;

          if (activePage?.data instanceof ContactSummaryTable) {
            await this.addContact();
          }

          if (activePage?.data instanceof ClientSummaryTable) {
            await this.addClient();
          }

          if (activePage?.data instanceof PaymentProfileSummaryTable) {
            await this.addPaymentProfile();
          }

          if (activePage?.data instanceof TermsConditionsSummaryTable) {
            await this.addTermsConditions();
          }

          return true;
        }
      },
      pageInitializer: () => getInitialPageManagers()
    };
    return new PageControl(options);
  }

  async addPaymentProfile(): Promise<boolean> {
    //payment profiles are reliant on supplier information
    if (!(await v6BootLoaderWithFailMsg())) {
      return false;
    }
    const paymentProfileContainer = await createNewPaymentProfile();
    if (paymentProfileContainer) {
      const qv = await this.paymentProfileFactory({
        title: () => LangFranchiseeClientListView.addPaymentProfile(),
        paymentProfile: paymentProfileContainer
      });

      await qv.showModal();

      await this.refreshPaymentProfileList();

      return true;
    }
    return false;
  }

  async addTermsConditions(): Promise<boolean> {
    //TaC are reliant on supplier information
    if (!(await v6BootLoaderWithFailMsg())) {
      return false;
    }
    const container = await createNewTermsConditions();
    if (container) {
      const qv = await this.termsConditionsFactory({
        title: () => LangFranchiseeClientListView.addTermsConditions(),
        termsConditions: container
      });

      await qv.showModal();

      await this.refreshTermsConditionsList();

      return true;
    }
    return false;
  }

  async addClient(): Promise<boolean> {
    const clientContainer = await createNewClient(this.clientOwnerId);
    if (clientContainer) {
      const qm = clientContainerManagerFactory(clientContainer);
      const qv = await this.clientViewFactory({
        viewTitle: () => LangFranchiseeClientListView.addClient(),
        clientContainerManager: qm
      });

      await qv.showModal();

      await this.refreshContactList();
      await this.refreshClientList();

      return true;
    }
    return false;
  }

  async addContact(): Promise<boolean> {
    const contactContainer = await createNewContact();
    if (contactContainer) {
      const qv = await this.contactViewFactory({
        title: () => LangFranchiseeClientListView.addContact(),
        contact: contactContainer
      });
      await qv.showModal();

      await this.refreshContactList();
      await this.refreshClientList();

      return true;
    }
    return false;
  }

  private async refreshClientList() {
    const tables = this.tables.filter(x => x instanceof ClientSummaryTable);

    for (const x of tables) {
      if (x.dataTable) await x.refreshData();
    }
  }

  private async refreshContactList() {
    const tables = this.tables.filter(x => x instanceof ContactSummaryTable);

    for (const x of tables) {
      if (x.dataTable) await x.refreshData();
    }
  }

  private async refreshPaymentProfileList() {
    const tables = this.tables.filter(x => x instanceof PaymentProfileSummaryTable);

    for (const x of tables) {
      if (x.dataTable) await x.refreshData();
    }
  }

  private async refreshTermsConditionsList() {
    const tables = this.tables.filter(x => x instanceof TermsConditionsSummaryTable);

    for (const x of tables) {
      if (x.dataTable) await x.refreshData();
    }
  }

  protected template(): EventTemplate {
    return html` <div id=${this.elementId} class="page-content">${this.pageControl.ui}</div>`;
  }

  private initializeTables(): (ClientSummaryTable | ContactSummaryTable | PaymentProfileSummaryTable | TermsConditionsSummaryTable)[] {
    return [
      new ClientSummaryTable({
        title: () => LangFranchiseeClientListView.clientPlural(),
        openContactEvent: async (contactSummary: ViewContactSummary) => await this.openContact(contactSummary),
        addButtonTitle: () => LangFranchiseeClientListView.createClient(),
        pageFragment: 'clients'
      }),
      new ContactSummaryTable({
        title: () => LangFranchiseeClientListView.contactPlural(),
        openContactEvent: async (contactSummary: ViewContactSummary) => await this.openContact(contactSummary),
        addButtonTitle: () => LangFranchiseeClientListView.createContact(),
        pageFragment: 'contacts'
      }),
      new PaymentProfileSummaryTable({
        title: () => LangFranchiseeClientListView.paymentProfilePlural(),
        openPaymentProfileEvent: async (paymentProfileSummary: PaymentProfileSummary) =>
          await this.openPaymentProfile(paymentProfileSummary),
        addButtonTitle: () => LangFranchiseeClientListView.createPaymentProfile(),
        pageFragment: 'paymentProfile',
        supplierId: singleSupplierId()
      }),
      new TermsConditionsSummaryTable({
        title: () => LangFranchiseeClientListView.termsAndConditions(),
        openEvent: async (termsConditionsSummary: TermsAndConditionsSummary) =>
          await this.openTermsConditions(termsConditionsSummary),
        addButtonTitle: () => LangFranchiseeClientListView.addTermsConditions(),
        pageFragment: 'termsConditions',
        supplierId: singleSupplierId()
      })
    ];
  }

  private async openPaymentProfile(paymentProfileSummary: PaymentProfileSummary) {
    const qv = await this.paymentProfileFactory({
      title: () => LangFranchiseeClientListView.editPaymentProfile({ name: paymentProfileSummary.name }),
      paymentProfile: new PaymentProfileContainer(paymentProfileSummary.id, null)
    });
    await qv.showModal();

    await this.refreshPaymentProfileList();
  }

  private async openTermsConditions(termsAndConditionsSummary: TermsAndConditionsSummary) {
    const qv = await this.termsConditionsFactory({
      title: () => LangFranchiseeClientListView.editTermsConditions({ name: termsAndConditionsSummary.name }),
      termsConditions: new TermsConditionsContainer(termsAndConditionsSummary.id, null)
    });
    await qv.showModal();

    await this.refreshTermsConditionsList();
  }

  private async openContact(contactSummary: ViewContactSummary) {
    const qv = await this.contactViewFactory({
      title: () => LangFranchiseeClientListView.editContact({ name: contactSummary.name }),
      contact: new ContactContainer(contactSummary.id, null, contactSummary.isPrimary)
    });
    await qv.showModal();

    await this.refreshContactList();
    await this.refreshClientList();
  }
}
